import {
  isDestroyed,
  isDestroying,
  registerDestructor,
} from '@ember/destroyable';
import { concat } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismBanner,
  PrismButton,
  PrismIcon,
  PrismModal,
  PrismRadioGroup,
} from '@mylighthouse/prism-ember';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import { task, timeout } from 'ember-concurrency';
import { t } from 'ember-intl';
import { and, eq, not, or } from 'ember-truth-helpers';
import type { Account } from 'frontend-payments/models';
import adyenDropin from 'frontend-payments/modifiers/adyen-dropin';
import type AjaxService from 'frontend-payments/services/ajax';
import {
  PAYMENT_METHOD_BANK_TRANSFER,
  PAYMENT_METHOD_CHECK,
  PAYMENT_METHOD_CREDIT_CARD,
  PAYMENT_METHOD_DIRECT_DEBIT,
  type PaymentMethod,
} from 'frontend-payments/utils/constants/payment-method-constants';

import styles from './modal-recurring-payment-method.module.css';

// Artificial timeout to allow payment method to sync to backend
const PAYMENT_METHOD_ADDED_TIMEOUT_MS = 5_000;

// Accepted direct debit payment method types
const ACCEPTED_DIRECT_DEBIT = [
  'ach',
  'directdebit_GB',
  'sepadirectdebit',
] as const;

interface AdyenDropinElement {
  handleAction: (action: { paymentMethodType: string }) => void;
  setStatus: (status: string) => void;
  submit: () => void; // This method doesn't return a promise, it triggers the onSubmit callback
}

interface AdyenPaymentState {
  data: unknown;
}

interface PaymentResponse {
  action?: {
    paymentMethodType: string;
  };
  refusalReasonCode?: string;
}

interface AdyenPaymentMethod {
  name: string;
  type: string;
}

interface PaymentMethodsResponse {
  paymentMethods: AdyenPaymentMethod[];
}

interface PaymentModalRecurringPaymentMethodSignature {
  Args: {
    account: Account;
    onCloseModal: () => void;
    onPaymentMethodSaved: () => void;
  };
}

export default class PaymentModalRecurringPaymentMethod extends Component<PaymentModalRecurringPaymentMethodSignature> {
  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked adyenDropin: AdyenDropinElement | null = null;
  @tracked isBacsActionHandled = false;
  @tracked selectedPaymentMethod: PaymentMethod = PAYMENT_METHOD_CREDIT_CARD;

  fetchPaymentMethodsTask = task(
    async (): Promise<PaymentMethodsResponse | null> => {
      try {
        const accountNumber = this.args.account.accountNumber;
        const url = `/api/v4/accounts/${accountNumber}/payment-methods`;

        const paymentMethodsParsed =
          await this.ajax.request<PaymentMethodsResponse>(url);
        return paymentMethodsParsed;
      } catch (error) {
        await this.alerting.showErrorAlert();
        this.errorTracking.captureException(error);
        return null;
      }
    },
  );

  saveRecurringPaymentMethodTask = task(
    async (paymentData: AdyenPaymentState): Promise<void> => {
      try {
        const accountNumber = this.args.account.accountNumber;
        const url = `/api/v4/accounts/${accountNumber}/payments`;

        const paymentParsed = await this.ajax.post<PaymentResponse>(url, {
          contentType: 'application/json',
          data: { adyen_data: paymentData.data },
        });

        // If the call didn't succeed, show an error message
        // e.g. card is detected as fraud, invalid card, ...
        if (paymentParsed.refusalReasonCode) {
          if (this.adyenDropin) {
            this.adyenDropin.setStatus('ready'); // This resets the state so the form becomes editable again
          }
          await this.alerting.showErrorAlert();
          return;
        }

        if (paymentParsed.action) {
          if (this.adyenDropin) {
            this.adyenDropin.handleAction(paymentParsed.action);
          }

          // Bacs has an extra step to download the DDI mandate for accountancy purposes
          // Switch our buttons to prevent double form submissions and prevent confusion,
          // as cancelling wouldn't actually cancel adding the payment method anymore
          if (paymentParsed.action.paymentMethodType === 'directdebit_GB') {
            this.isBacsActionHandled = true;
            this.showSuccessToast();
          }
        } else {
          // Wait for backend processing
          await timeout(PAYMENT_METHOD_ADDED_TIMEOUT_MS);

          this.showSuccessToast();

          this.usageAnalytics.track('Payment Method Added', {
            eventTrigger: 'payment-method-modal',
          });

          this.args.onPaymentMethodSaved();
        }
      } catch (error) {
        this.errorTracking.captureException(error);
        await this.alerting.showErrorAlert();
      }
    },
  );

  submitDetailsTask = task(
    async (
      state: AdyenPaymentState,
      dropin: AdyenDropinElement,
    ): Promise<void> => {
      try {
        const accountNumber = this.args.account.accountNumber;
        const url = `/api/v4/accounts/${accountNumber}/payments/details`;

        const paymentDetailsParsed = await this.ajax.post<PaymentResponse>(
          url,
          {
            contentType: 'application/json',
            data: { adyen_data: state.data },
          },
        );

        // If the call didn't succeed, show an error message
        // e.g. card is detected as fraud, invalid card, ...
        if (paymentDetailsParsed.refusalReasonCode) {
          dropin.setStatus('ready'); // This resets the state so the form becomes editable again
          await this.alerting.showErrorAlert();
          return;
        }

        await timeout(PAYMENT_METHOD_ADDED_TIMEOUT_MS);

        this.showSuccessToast();

        this.usageAnalytics.track('Payment Method Added', {
          eventTrigger: 'payment-method-modal',
        });

        this.args.onPaymentMethodSaved();
      } catch (error) {
        this.errorTracking.captureException(error);
        await this.alerting.showErrorAlert();
      }
    },
  );

  get availablePaymentMethods(): PaymentMethod[] {
    return this.args.account?.payments?.availablePaymentMethods || [];
  }

  get filteredPaymentMethods(): PaymentMethodsResponse | null {
    const allPaymentMethods =
      this.fetchPaymentMethodsTask.lastSuccessful?.value;

    if (!allPaymentMethods?.paymentMethods) {
      return null;
    }

    const filterPredicates: {
      [key in PaymentMethod]: (method: AdyenPaymentMethod) => boolean;
    } = {
      [PAYMENT_METHOD_CREDIT_CARD]: (method) => method.name === 'Credit Card',
      [PAYMENT_METHOD_DIRECT_DEBIT]: (method) =>
        ACCEPTED_DIRECT_DEBIT.includes(
          method.type as (typeof ACCEPTED_DIRECT_DEBIT)[number],
        ),
      [PAYMENT_METHOD_CHECK]: () => false,
      [PAYMENT_METHOD_BANK_TRANSFER]: () => false,
    };

    const filterFn =
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      filterPredicates[this.selectedPaymentMethod] ?? (() => true);

    const filteredMethods = allPaymentMethods.paymentMethods.filter(filterFn);

    // Return the full Adyen response object with filtered payment methods
    // This ensures the dropin gets all necessary configuration and metadata
    return {
      ...allPaymentMethods,
      paymentMethods: filteredMethods,
    };
  }

  get hasAvailablePaymentMethods(): boolean {
    return this.availablePaymentMethods.length > 0;
  }

  // Bacs is a system for making payments directly from one bank account to another.
  // It is commonly used in the UK for direct debit payments, where customers authorize
  // businesses to collect payments directly from their bank accounts on agreed dates.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isBacs() {
    return this.filteredPaymentMethods?.paymentMethods?.some(
      (paymentMethod) => paymentMethod.type === 'directdebit_GB',
    );
  }

  get isCreditCardAvailable(): boolean {
    return this.availablePaymentMethods.includes(PAYMENT_METHOD_CREDIT_CARD);
  }

  get isDirectDebitAvailable(): boolean {
    return this.availablePaymentMethods.includes(PAYMENT_METHOD_DIRECT_DEBIT);
  }

  constructor(
    owner: Owner,
    args: PaymentModalRecurringPaymentMethodSignature['Args'],
  ) {
    super(owner, args);
    // Fetch payment methods when component is created (only need to fetch once)
    void this.fetchPaymentMethodsTask.perform();
  }

  showSuccessToast(): void {
    let successMessage = this.intl.t('payments.methods.added_success');

    if (this.selectedPaymentMethod === PAYMENT_METHOD_CREDIT_CARD) {
      successMessage = this.intl.t(
        'payments.methods.updated_credit_card_succes',
      );
    } else if (this.selectedPaymentMethod === PAYMENT_METHOD_DIRECT_DEBIT) {
      successMessage = this.intl.t(
        'payments.methods.updated_direct_debit_succes',
      );
    }

    this.toast.success(successMessage, {
      timeout: TOAST_TIMEOUT_LONG,
      showIcon: true,
    });
  }

  @action
  async onAdditionalDetails(state: unknown): Promise<void> {
    if (this.adyenDropin) {
      await this.submitDetailsTask.perform(
        state as AdyenPaymentState,
        this.adyenDropin,
      );
    }
  }

  @action
  onAdyenDropinMounted(adyenDropin: unknown): void {
    if (isDestroyed(this) || isDestroying(this)) {
      console.warn(
        'Component is destroyed/destroying, not mounting Adyen dropin',
      );
      return;
    }

    this.adyenDropin = adyenDropin as AdyenDropinElement;

    registerDestructor(this, () => {
      this.adyenDropin = null;
    });
  }

  @action
  async onSubmit(state: unknown): Promise<void> {
    await this.saveRecurringPaymentMethodTask.perform(
      state as AdyenPaymentState,
    );
  }

  @action
  saveRecurringPaymentMethod(): void {
    if (this.adyenDropin) {
      this.adyenDropin.submit();
    }
  }

  @action
  updatePaymentMethodOption(event: Event): void {
    const updatedOption = (event.target as HTMLInputElement).value;
    this.selectedPaymentMethod = updatedOption as PaymentMethod;
  }

  <template>
    <PrismModal
      @onClose={{@onCloseModal}}
      @title={{t "payments.recurring_method.recurring_payment"}}
    >
      <:default>
        <div class="modal--small modal--padded">
          {{#if this.isBacs}}
            <div class="mb-300">
              <PrismBanner
                @bannerMessage={{t "payments.bacs.verification.message"}}
                @bannerStyle="info"
                @bannerTitle={{t "payments.bacs.verification.title"}}
              />
            </div>
          {{/if}}
          {{#if this.hasAvailablePaymentMethods}}
            <PrismRadioGroup
              @name="recurring-payment-radio-group"
              @onChange={{this.updatePaymentMethodOption}}
              @value={{this.selectedPaymentMethod}}
              as |RadioGroup|
            >
              {{#if this.isCreditCardAvailable}}
                <div class={{styles.input-container}}>
                  <RadioGroup.input @value={{PAYMENT_METHOD_CREDIT_CARD}}>
                    <div class="flex items-center gap-100">
                      <PrismIcon @name="credit-card" @size="small" />
                      {{t "payments.payment_method.credit_card"}}
                    </div>
                  </RadioGroup.input>

                  {{! Outside RadioGroup.input to avoid indentation }}
                  {{#if
                    (and
                      this.filteredPaymentMethods
                      (eq this.selectedPaymentMethod PAYMENT_METHOD_CREDIT_CARD)
                    )
                  }}
                    <div
                      {{adyenDropin
                        adyenPaymentMethods=this.filteredPaymentMethods
                        onSubmit=this.onSubmit
                        onAdditionalDetails=this.onAdditionalDetails
                        onAdyenDropinMounted=this.onAdyenDropinMounted
                      }}
                    ></div>
                  {{/if}}
                </div>
              {{/if}}

              {{#if this.isDirectDebitAvailable}}
                <div class={{styles.input-container}}>
                  <RadioGroup.input @value={{PAYMENT_METHOD_DIRECT_DEBIT}}>
                    <div class="flex items-center gap-100">
                      <PrismIcon @name="credit-card" @size="small" />
                      {{t "payments.payment_method.direct_debit"}}
                    </div>
                  </RadioGroup.input>

                  {{! Outside RadioGroup.input to avoid indentation }}
                  {{#if
                    (and
                      this.filteredPaymentMethods
                      (eq
                        this.selectedPaymentMethod PAYMENT_METHOD_DIRECT_DEBIT
                      )
                    )
                  }}
                    <div
                      {{adyenDropin
                        adyenPaymentMethods=this.filteredPaymentMethods
                        onSubmit=this.onSubmit
                        onAdditionalDetails=this.onAdditionalDetails
                        onAdyenDropinMounted=this.onAdyenDropinMounted
                      }}
                    ></div>
                  {{/if}}
                </div>
              {{/if}}
            </PrismRadioGroup>
          {{else}}
            <div class="p-200 text-center">
              <p class="text-muted mb-200">
                {{t "payments.methods.title.no_active_method"}}
              </p>
            </div>
          {{/if}}
        </div>
      </:default>
      <:footer>
        <div class={{concat styles.footer " " "flex justify-end"}}>
          <PrismButton
            @buttonType="secondary"
            @label={{t "general.cancel"}}
            @onClick={{@onCloseModal}}
          />
          <PrismButton
            @isDisabled={{or
              (not this.hasAvailablePaymentMethods)
              (not this.filteredPaymentMethods)
              this.fetchPaymentMethodsTask.isRunning
              this.saveRecurringPaymentMethodTask.isRunning
            }}
            @isLoading={{this.saveRecurringPaymentMethodTask.isRunning}}
            @label={{t "payments.recurring_method.set_recurring_payment"}}
            @onClick={{this.saveRecurringPaymentMethod}}
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
